// Контекст сессии/текущего пользователя (SPEC §4.6). Держит SelfUser (нужен экранам для «своё/чужое»,
// упоминаний, гейтинга админки) и команды login/logout/refresh. Сетевое — через transport.rest
// (auth — не-стор-данные, SPEC §4.0). Релогин по `auth.required` ведёт стор (select.needRelogin),
// здесь — действия входа/выхода и кеш текущего профиля.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { SelfUser } from '@tele-chat/shared-contracts'
import { useTransport } from '../transport/TransportContext.js'

export interface SessionValue {
  /** Текущий пользователь; null — не аутентифицирован (показываем экран входа). */
  self: SelfUser | null
  /** true, пока идёт первичная проверка сессии (me()). */
  checking: boolean
  /** Залогиниться: ставит cookie (браузером, D-07), поднимает живой канал, кеширует self. */
  login(email: string, password: string): Promise<void>
  /** Перечитать профиль (после updateMe/смены роли). */
  refresh(): Promise<void>
  /** Выйти на этом устройстве. */
  logout(): Promise<void>
  /** Применить уже полученный SelfUser (напр. из ответа login). */
  setSelf(self: SelfUser | null): void
  /**
   * PWD-4: временный пароль, которым только что вошли (обязательная смена). Только в памяти вкладки —
   * не в хранилище; после перезагрузки страницы null (экран спросит его полем).
   */
  temporaryPassword: string | null
  /** PWD-4: сменить пароль, заданный админом, на свой → перечитать профиль (снимает обязательную смену). */
  completePasswordChange(currentPassword: string, newPassword: string): Promise<void>
}

const SessionContext = createContext<SessionValue | null>(null)

export interface SessionProviderProps {
  /** Начальный self (из стартового me() в App, чтобы не дёргать сеть повторно). */
  initialSelf: SelfUser | null
  /** true, если стартовая проверка ещё идёт. */
  initialChecking?: boolean
  /** Колбэк «канал поднят» — App возобновляет transport.start()/store.attach после login. */
  onAuthenticated?(self: SelfUser): Promise<void> | void
  /**
   * PWD-4: пароль сменён после обязательной смены — App перечитывает список чатов в стор
   * (стартовая синхронизация, пока API было закрыто, получила 403 и пропущена).
   */
  onPasswordChanged?(self: SelfUser): Promise<void> | void
  children: ReactNode
}

export function SessionProvider({
  initialSelf,
  initialChecking = false,
  onAuthenticated,
  onPasswordChanged,
  children,
}: SessionProviderProps): JSX.Element {
  const transport = useTransport()
  const [self, setSelf] = useState<SelfUser | null>(initialSelf)
  const [checking, setChecking] = useState(initialChecking)
  const temporaryPasswordRef = useRef<string | null>(null)

  // App ведёт стартовый boot (hydrate→attach→start→me) и прокидывает результат через initialSelf/
  // initialChecking. Синхронизируем их в локальное состояние, чтобы AuthGate увидел итог проверки
  // сессии (без этого initialSelf=useState seed «застывал» бы на старте). login/logout дальше ведут self сами.
  useEffect(() => {
    setSelf(initialSelf)
  }, [initialSelf])
  useEffect(() => {
    setChecking(initialChecking)
  }, [initialChecking])

  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      const user = await transport.rest.login({ email, password })
      temporaryPasswordRef.current = user.mustChangePassword === true ? password : null
      setSelf(user)
      await onAuthenticated?.(user)
    },
    [transport, onAuthenticated],
  )

  const refresh = useCallback(async (): Promise<void> => {
    setChecking(true)
    try {
      const user = await transport.rest.me()
      setSelf(user)
    } catch {
      setSelf(null)
    } finally {
      setChecking(false)
    }
  }, [transport])

  const logout = useCallback(async (): Promise<void> => {
    temporaryPasswordRef.current = null
    try {
      await transport.rest.logout()
    } finally {
      setSelf(null)
    }
  }, [transport])

  const completePasswordChange = useCallback(
    async (currentPassword: string, newPassword: string): Promise<void> => {
      await transport.rest.changePassword({ oldPassword: currentPassword, newPassword })
      temporaryPasswordRef.current = null
      const user = await transport.rest.me()
      setSelf(user)
      await onPasswordChanged?.(user)
    },
    [transport, onPasswordChanged],
  )

  const value = useMemo<SessionValue>(
    () => ({
      self,
      checking,
      login,
      refresh,
      logout,
      setSelf,
      temporaryPassword: temporaryPasswordRef.current,
      completePasswordChange,
    }),
    // temporaryPasswordRef меняется вместе с self (login/смена) — пересборка по self достаточна
    [self, checking, login, refresh, logout, completePasswordChange],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

/** Текущая сессия. Бросает вне SessionProvider. */
export function useSession(): SessionValue {
  const ctx = useContext(SessionContext)
  if (ctx === null) {
    throw new Error('useSession(): компонент должен быть внутри <SessionProvider>')
  }
  return ctx
}

/** Текущий пользователь (или null). Удобный шорткат. */
export function useSelf(): SelfUser | null {
  return useSession().self
}
